/**
 * Accès aux routes `/api/leads` : types, clés de cache, hooks et mise en forme.
 *
 * Ce fichier vit dans `composants/` et non dans `api/hooks.ts` par pure
 * circonstance : `hooks.ts` est écrit en parallèle par les écrans de contenu,
 * et deux plumes sur un même fichier ne produisent que des conflits. Les
 * conventions de `hooks.ts` sont reprises telles quelles — clés regroupées,
 * types recopiés depuis le contrat HTTP, mutations qui invalident ce qu'elles
 * viennent de rendre faux.
 *
 * Une exception assumée à la règle « on recopie le contrat HTTP » : les tables
 * de libellés viennent de `@delsa/shared`. Elles sont partagées avec l'email de
 * notification des demandes ; deux copies afficheraient un jour deux budgets
 * différents pour la même demande.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ErreurApi } from "../api/client";
import { api } from "../api/client";

export type { StatutLead } from "@delsa/shared/lead";
export {
  LIBELLES_BUDGET,
  LIBELLES_DELAI,
  LIBELLES_PROJET,
  LIBELLES_STATUT_LEAD,
  STATUTS_LEAD,
  libelle,
} from "@delsa/shared/lead";

import type { StatutLead } from "@delsa/shared/lead";

// ---------------------------------------------------------------------------
// Formes renvoyées par l'API
// ---------------------------------------------------------------------------

/** Ce que rend `vueLead` côté API : le lead sans son hachage d'IP. */
export type Lead = {
  id: number;
  createdAt: string;
  nom: string;
  email: string;
  telephone: string;
  communeTexte: string;
  typeProjet: string;
  pieces: string[];
  surface: number | null;
  budget: string;
  delai: string;
  message: string;
  pageSource: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  statut: string;
  notes: string;
  clientId: number | null;
  userAgent: string;
};

export type EvenementStatut = {
  id: number;
  ancienStatut: string;
  nouveauStatut: string;
  /** Vide si le compte à l'origine du changement a été supprimé depuis. */
  auteur: string;
  createdAt: string;
};

export type FicheLead = {
  lead: Lead;
  historique: EvenementStatut[];
};

/** Période demandée, bornes incluses, au format « AAAA-MM-JJ ». */
export type Periode = {
  depuis?: string;
  jusqua?: string;
};

export const clesLeads = {
  liste: (periode: Periode) => ["leads", periode.depuis ?? "", periode.jusqua ?? ""] as const,
  fiche: (id: number) => ["lead", id] as const,
};

// ---------------------------------------------------------------------------
// Lecture
// ---------------------------------------------------------------------------

/**
 * Liste des demandes de la période.
 *
 * Seule la période est envoyée à l'API. Le statut, la commune et le tri sont
 * appliqués dans le navigateur : la vue kanban a besoin de **toutes** les
 * colonnes à la fois, et un filtre par statut côté serveur la viderait de cinq
 * sixièmes de son contenu. Sur quelques centaines de demandes, filtrer en
 * mémoire est instantané et évite un aller-retour à chaque frappe.
 */
export function useLeads(periode: Periode) {
  return useQuery({
    queryKey: clesLeads.liste(periode),
    queryFn: () => {
      const parametres = new URLSearchParams({ limite: "1000" });
      if (periode.depuis) parametres.set("depuis", periode.depuis);
      if (periode.jusqua) parametres.set("jusqua", periode.jusqua);
      return api.get<{ leads: Lead[] }>(`/api/leads?${parametres}`);
    },
  });
}

export function useLead(id: number) {
  return useQuery({
    queryKey: clesLeads.fiche(id),
    queryFn: () => api.get<FicheLead>(`/api/leads/${id}`),
    enabled: Number.isFinite(id) && id > 0,
  });
}

// ---------------------------------------------------------------------------
// Écriture
// ---------------------------------------------------------------------------

type Suivi = { id: number; statut?: StatutLead; notes?: string };

/** Instantané des listes en cache, conservé le temps d'une écriture optimiste. */
type ListesLeads = [readonly unknown[], { leads: Lead[] } | undefined][];

/**
 * Change le statut ou les notes d'une demande.
 *
 * Le cache de la fiche est écrit depuis la réponse, qui contient déjà la fiche
 * à jour : la relire coûterait un aller-retour pour apprendre ce que l'on
 * vient de recevoir. Les listes, elles, sont invalidées — on ne sait pas
 * lesquelles sont en mémoire ni quelle période elles couvrent.
 */
export function useSuivreLead() {
  const cache = useQueryClient();

  return useMutation<FicheLead, ErreurApi, Suivi, ListesLeads>({
    mutationFn: ({ id, ...champs }) => api.patch<FicheLead>(`/api/leads/${id}`, champs),

    // Le kanban repose là-dessus : une carte lâchée dans une colonne doit y
    // rester tout de suite. Attendre la réponse la ferait revenir en arrière
    // le temps d'un aller-retour, ce que l'œil lit comme un raté du glisser.
    onMutate: async ({ id, statut }) => {
      if (statut === undefined) return [];

      await cache.cancelQueries({ queryKey: ["leads"] });
      const listes = cache.getQueriesData<{ leads: Lead[] }>({ queryKey: ["leads"] });

      for (const [clef, valeur] of listes) {
        if (!valeur) continue;
        cache.setQueryData(clef, {
          leads: valeur.leads.map((lead) => (lead.id === id ? { ...lead, statut } : lead)),
        });
      }

      return listes;
    },

    // L'API a refusé : on remet exactement ce qui était affiché avant, plutôt
    // que de laisser une carte dans une colonne où elle n'est pas.
    onError: (_erreur, _variables, listes) => {
      for (const [clef, valeur] of listes ?? []) cache.setQueryData(clef, valeur);
    },

    onSuccess: (fiche) => {
      cache.setQueryData(clesLeads.fiche(fiche.lead.id), fiche);
    },

    onSettled: () => {
      cache.invalidateQueries({ queryKey: ["leads"] });
    },
  });
}

export function useConvertirLead() {
  const cache = useQueryClient();

  return useMutation<{ clientId: number } & FicheLead, ErreurApi, number>({
    mutationFn: (id) => api.post<{ clientId: number } & FicheLead>(`/api/leads/${id}/convertir`),
    onSuccess: (reponse) => {
      cache.setQueryData(clesLeads.fiche(reponse.lead.id), {
        lead: reponse.lead,
        historique: reponse.historique,
      });
      cache.invalidateQueries({ queryKey: ["leads"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Mise en forme
// ---------------------------------------------------------------------------

/**
 * L'API stocke ses horodatages au format « 2026-08-16 19:30:00 », en UTC et
 * sans suffixe de fuseau. Passé tel quel à `new Date`, JavaScript le lirait
 * comme une heure locale et décalerait tout d'une ou deux heures selon la
 * saison. Le « T » et le « Z » rétablissent la lecture correcte.
 */
export function versDate(horodatage: string): Date | null {
  const date = new Date(`${horodatage.replace(" ", "T")}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formaterHorodatage(horodatage: string): string {
  const date = versDate(horodatage);
  if (!date) return horodatage;
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeStyle: "short" }).format(date);
}

export function formaterDate(horodatage: string): string {
  const date = versDate(horodatage);
  if (!date) return horodatage;
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "short" }).format(date);
}

/**
 * `+33612345678` → `06 12 34 56 78`.
 *
 * L'affichage seul est reformaté ; le lien `tel:` garde la forme E.164, la
 * seule que tous les téléphones composent sans se tromper depuis l'étranger.
 * Un numéro d'une autre forme est rendu tel quel plutôt que découpé au hasard.
 */
export function formaterTelephone(numero: string): string {
  if (!/^\+33\d{9}$/.test(numero)) return numero;
  const national = `0${numero.slice(3)}`;
  return national.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
}

/** Bornes « AAAA-MM-JJ » d'une période exprimée en jours révolus. */
export function periodeDepuis(jours: number): Periode {
  const debut = new Date();
  debut.setDate(debut.getDate() - jours);
  return { depuis: debut.toISOString().slice(0, 10) };
}
